import { useParams } from 'react-router-dom';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, useMediaQuery } from '@mui/material';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../../loading-page';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import ConversionService from '../../../../shared/services/conversion/conversion.service';
import SquareFootConversionService from '../../../../shared/services/square-foot-conversion/square-foot-conversion.service';
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service';
import { IConversionSourceWithConversions, IConversionYear } from '../../../../shared/services/conversion-source/conversion-source.model';
import gaiaTheme from '../../../../root-components/theme';
import { findClosestYear } from '../../../../shared/utils/misc-shared-utils';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { IPostRule } from '../../../../shared/services/rule/rule.model';
import RuleService from '../../../../shared/services/rule/rule.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { QuickRuleDialog } from '../../../dialogs/settings/data-import/quick-rule-dialog';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../../hooks/use-debounce-hook';
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils';

export const SettingsConversionFactorsPage = () => {
  const { conversionSourceId } = useParams();
  //Services
  const ruleService = new RuleService();
  const conversionSourceService = new ConversionSourceService();
  const conversionService = new ConversionService();
  const squareFootConversionService = new SquareFootConversionService();
  const spendBasedConversionService = new SpendBasedConversionService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  //State
  const [conversionSource, setConversionSource] = useState<IConversionSourceWithConversions>();
  const [selectedYear, setSelectedYear] = useState<string>();
  const [conversions, setConversions] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedConversion, setSelectedConversion] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [errorCode, setErrorCode] = useState<number>();
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [change, setChange] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>();
  const [order, setOrder] = useState<'asc' | 'desc'>();
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false);
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    if (conversionSourceId) {
      conversionSourceService.getOne(conversionSourceId, `?includeYears=true`)
        .then(response => {
          setConversionSource(response);
          if (response.conversionYears && response.conversionYears.length > 0) {
            const activeYears = response.conversionYears
              .filter((yearObj: IConversionYear) => yearObj.active || yearObj.isActive)
              .map((yearObj: IConversionYear) => yearObj.year);
            if (activeYears.length > 0) {
              const closestYear = findClosestYear(activeYears);
              setSelectedYear(closestYear);
            }
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
  }, [conversionSourceId]);

  useEffect(() => {
    getConversions();
  }, [selectedYear, conversionSource, inputPage, perPage, change]);

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1);
      } else {
        setInputPage(0);
      }
    }
    setHasPageMounted(true);
  }, [order, orderBy, selectedYear, debouncedSearchQuery]);

  const getConversions = () => {
    if (selectedYear && conversionSource) {
      requestId.current++
      const currentRequestId = requestId.current
      const resolvedConversionType: 'conversion' | 'squareFootConversion' | 'spendBasedConversion' = (conversionSource.conversionType as any) ?? 'conversion';
      if (resolvedConversionType === 'conversion') {
        conversionService
          .getConversionsForConversionSourceWithPagination(
            conversionSource.id,
            selectedYear,
            debouncedSearchQuery,
            inputPage,
            perPage,
            orderBy,
            order,
          )
          .then(response => {
            if (currentRequestId === requestId.current) {
              setIsLoading(false);
              setConversions(response.rows);
              setTotalRecords(response.count);
            }
          })
          .catch(error => {
            if (currentRequestId === requestId.current) {
              console.error(error);
              setIsLoading(false);
            }
          });
      } else if (resolvedConversionType === 'squareFootConversion') {
        squareFootConversionService
          .getSquareFootConversionsForConversionSourceWithPagination(
            conversionSource.id,
            selectedYear,
            debouncedSearchQuery,
            inputPage,
            perPage,
            orderBy,
            order,
          )
          .then(response => {
            if (currentRequestId === requestId.current) {
              setIsLoading(false);
              setConversions(response.rows);
              setTotalRecords(response.count);
            }
          })
          .catch(error => {
            if (currentRequestId === requestId.current) {
              console.error(error);
              setIsLoading(false);
            }
          });
      } else if (resolvedConversionType === 'spendBasedConversion') {
        spendBasedConversionService
          .getSpendBasedConversionsForConversionSourceWithPagination(
            conversionSource.id,
            selectedYear,
            debouncedSearchQuery,
            inputPage,
            perPage,
            orderBy,
            order,
          )
          .then(response => {
            if (currentRequestId === requestId.current) {
              setIsLoading(false);
              setConversions(response.rows);
              setTotalRecords(response.count);
            }
          })
          .catch(error => {
            if (currentRequestId === requestId.current) {
              console.error(error);
              setIsLoading(false);
            }
          });
      }
    }
  };

  const headCellsConversion: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false },
    { id: 'scope', numeric: false, label: 'Scope', sortable: false },
    { id: 'detailLevelOne', numeric: false, label: 'Level One', sortable: true },
    { id: 'detailLevelTwo', numeric: false, label: 'Level Two', sortable: true },
    { id: 'detailLevelThree', numeric: false, label: 'Level Three', sortable: true },
    { id: 'detailLevelFour', numeric: false, label: 'Level Four', sortable: true },
    { id: 'detailLevelFive', numeric: false, label: 'Level Five', sortable: true },
    { id: 'measurementUnit', numeric: false, label: 'Unit of Measurement', sortable: true },
    { id: 'energyContentFactor', numeric: true, label: 'Energy Content Factor', sortable: false },
    { id: 'volumeMassConverter', numeric: true, label: 'Volume/Mass Converter', sortable: false },
    { id: 'multiplier', numeric: true, label: 'Multiplier', sortable: true },
    { id: 'greenHouseGasUnit', numeric: false, label: 'Greenhouse Gas Unit', sortable: true },
  ];

  const rowsConversion = !conversions
    ? []
    : conversions.map(conversion => {
      return {
        id: conversion.id,
        scope: conversion.scope,
        detailLevelOne: conversion.detailLevelOne,
        detailLevelTwo: conversion.detailLevelTwo,
        detailLevelThree: conversion.detailLevelThree,
        detailLevelFour: conversion.detailLevelFour,
        detailLevelFive: conversion.detailLevelFive,
        measurementUnit: conversion.measurementUnit.name,
        greenHouseGasUnit: conversion.greenHouseGasUnit.name,
        energyContentFactor: conversion.energyContentFactor,
        volumeMassConverter: conversion.volumeMassConverter,
        multiplier: conversion.multiplier,
        action: (
          <TableActionButtons
            id={conversion.id}
            includeOther={true}
            otherButtonIcon={IconName.StarBorderOutlined}
            otherButtonText={'Create Rule'}
            onOther={() => {
              setSelectedConversion(conversion);
              setOpenDialog(true);
            }}
          />
        )
      };
    });

  const headCellsSquareFoot: IHeadCell[] = [
    { id: 'activity', numeric: false, label: 'Activity', sortable: true },
    { id: 'consumptionTypeCode', numeric: false, label: 'Consumption Type', sortable: true },
    { id: 'measurementUnit', numeric: false, label: 'Unit of Measurement', sortable: true },
    { id: 'multiplier', numeric: true, label: 'Multiplier', sortable: true },
    { id: 'greenHouseGasUnit', numeric: false, label: 'Greenhouse Gas Unit', sortable: true },
  ];

  const rowsSquareFoot = !conversions
    ? []
    : conversions.map(conversion => {
      return {
        id: conversion.id,
        activity: conversion.activity,
        consumptionTypeCode: conversion.consumptionTypeCode === 'GAS' ? 'Gas' : 'Electricity',
        measurementUnit: conversion.measurementUnit.name,
        multiplier: conversion.multiplier,
        greenHouseGasUnit: conversion.greenHouseGasUnit.name,
      };
    });

  const headCellsSpendBased: IHeadCell[] = [
    { id: 'localAuthorityCustomIdentifier', numeric: false, label: 'Code', sortable: false },
    { id: 'activity', numeric: false, label: 'Activity', sortable: true },
    { id: 'category', numeric: false, label: 'Category', sortable: false },
    { id: 'measurementUnit', numeric: false, label: 'Unit of Measurement', sortable: true },
    { id: 'multiplier', numeric: true, label: 'Multiplier', sortable: true },
    { id: 'greenHouseGasUnit', numeric: false, label: 'Greenhouse Gas Unit', sortable: true },
  ];

  const rowsSpendBased = !conversions
    ? []
    : conversions.map(conversion => {
      return {
        id: conversion.id,
        localAuthorityCustomIdentifier: conversion.localAuthorityCustomIdentifier,
        activity: conversion.activity,
        category: getCategoryDisplayName(conversion.category) || '—',
        measurementUnit: conversion.measurementUnit.name,
        multiplier: conversion.multiplier,
        greenHouseGasUnit: conversion.greenHouseGasUnit.name,
      };
    });

  const handleOrderChange = (newOrderBy: string, newOrder: 'asc' | 'desc') => {
    setOrderBy(newOrderBy);
    setOrder(newOrder);
  };

  const selectConversionYearHandler = (event: SelectChangeEvent<string>) => {
    setSelectedYear(event.target.value);
  };

  const submitDialogHandler = (values: IPostRule, resetForm: () => void) => {
    if (!isDialogSubmitting) {
      setIsDialogSubmitting(true)
      ruleService.create(values)
        .then(() => {
          getConversions()
          resetForm();
          setOpenDialog(false)
          setSelectedConversion(null)
          userNotificationsContext.openSnackbar({ open: true, message: `Saved successfully` });
          setIsDialogSubmitting(false)
        })
        .catch(err => {
          if (err.response.data.message) {
            setErrorMessage(err.response.data.message)
            setErrorCode(err.response.data.code)
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
          console.log(err)
          setIsDialogSubmitting(false)
        })
    } else {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
    }
  }

  return (
    <PageOuterContainer>
      {conversionSource &&
        <PageHeading
          title={conversionSource.source}
          hasTabs={false}
          includeBackButton={true}
          backButtonLink='/settings/company-settings/conversion-factors'
          backButtonText='Back to conversion factors'
        />}
      {conversionSource &&
        <Grid container spacing={2} sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Grid item xl={3} lg={3} md={3} sm={12} xs={12}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={12} xs={12}>
            <FormControl fullWidth variant='outlined' >
              <InputLabel htmlFor='conversionYear'>Conversion Year</InputLabel>
              <Select id='conversionYear' name='conversionYear' label='Conversion Year'
                value={selectedYear}
                size='small'
                onChange={selectConversionYearHandler}>
                {conversionSource.conversionYears
                  .filter((yearObj: IConversionYear) => yearObj.active || yearObj.isActive)
                  .sort((a: IConversionYear, b: IConversionYear) => parseInt(a.year) - parseInt(b.year))
                  .map((yearObj: IConversionYear, index) => (
                    <MenuItem key={index} value={yearObj.year}>{`${yearObj.year}`}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      }
      <LoadingPage pageReady={!isLoading}>
        {conversionSource &&
          <TableDisplay
            onOrderChange={handleOrderChange}
            isMobileMode={isMobileMode}
            headCells={(conversionSource.conversionType ?? 'conversion') === 'conversion' ? headCellsConversion : (conversionSource.conversionType ?? 'conversion') === 'squareFootConversion' ? headCellsSquareFoot : headCellsSpendBased}
            rows={(conversionSource.conversionType ?? 'conversion') === 'conversion' ? rowsConversion : (conversionSource.conversionType ?? 'conversion') === 'squareFootConversion' ? rowsSquareFoot : rowsSpendBased}
            selectByIcon={true}
            selected={selected}
            setSelected={setSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage} />}
      </LoadingPage>
      {openDialog && selectedConversion && conversionSourceId &&
        <QuickRuleDialog
          open={openDialog}
          closeHandler={() => {
            setOpenDialog(false);
            setSelectedConversion(null);
          }}
          isEdit={false}
          isSubmitting={false}
          onSubmit={submitDialogHandler}
          errorMessage={errorMessage}
          errorCode={errorCode}
          conversionData={{
            categoryName: selectedConversion.category.name,
            categoryId: selectedConversion.category.id,
            detailLevelOne: selectedConversion.detailLevelOne,
            detailLevelTwo: selectedConversion.detailLevelTwo,
            detailLevelThree: selectedConversion.detailLevelThree,
            detailLevelFour: selectedConversion.detailLevelFour,
            detailLevelFive: selectedConversion.detailLevelFive,
            measurementUnitName: selectedConversion.measurementUnit.name,
            conversionSourceId: conversionSourceId,
          }}
        />
      }
    </PageOuterContainer>
  );
};